import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ReactElement,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  useForm,
  UseFormRegister,
  UseFormHandleSubmit,
  FieldErrors,
} from "react-hook-form";
import { useOutletContext } from "react-router";
import { useApi } from "@platform/api";
import { Alert, Button, TextField, Typography } from "@platform/design-system";

import { Loader } from "../../../components";
import { useAnalystTeams } from "../../../hooks";
import { ONE_DIGITAL } from "../../../constants";
import { extractErrorMessage, isValidEmail } from "../../../utils";
import { CommonContext } from "../types";

import type { AxiosInstance, AxiosResponse } from "axios";

type ConsultantUpdateRequest = {
  firstName: string;
  lastName: string;
  email: string;
};

const configKeysValues: Record<string, string> = {
  clientExecutive: "Client Executive",
  practice: "Practice",
  corporateEntity: "Corporate Entity",
  officeLocation: "Office Location",
  centro_select_carrier_enabled: "Centro Carrier Selection",
  new_analyst_workspace: "New Analyst Workspace",
  onedigitalOffice: "ONEDIGITAL Office",
};

const CENTRO_SELECT_CARRIER_ENABLED = "centro_select_carrier_enabled";

type ConfigData = Record<string, boolean>;

type OfficeLocation = {
  id: number;
  location: string;
};

type OfficeConfig = {
  mode: string | null;
  officeIds: number[];
};

type BrokerData = {
  analystTeamId?: number;
  consultant?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  type?: string;
};

type BrokerProperties = {
  id?: number;
  brokerId?: number;
  minutesPerPlan?: number;
  rfpPrepHours?: number;
  peerReviewHours?: number;
  additionalHours?: number;
  gapAnalysis?: number;
  asoOverride?: number;
  adaOverride?: number;
  dentalDisruptionOverride?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
};

type UpdateBrokerPropertiesRequest = {
  minutesPerPlan?: number;
  rfpPrepHours?: number;
  peerReviewHours?: number;
  additionalHours?: number;
  gapAnalysis?: number;
  asoOverride?: number;
  adaOverride?: number;
  dentalDisruptionOverride?: number;
};

const HIDDEN_CONFIG_KEYS = new Set([CENTRO_SELECT_CARRIER_ENABLED]);

const Configuration = (): ReactElement => {
  const { brokerId } = useOutletContext<CommonContext>();
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);
  const [localProperties, setLocalProperties] =
    useState<UpdateBrokerPropertiesRequest>({});
  const axiosInstance: AxiosInstance = useApi();
  const queryClient = useQueryClient();
  const brokerConfigKey = ["brokerClientInfoConfig", brokerId];
  const officeConfigKey = ["brokerOfficeConfig", brokerId];
  const allOfficesKey = ["allOneDigitalOffices"];
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [officeMode, setOfficeMode] = useState<string | null>(null);
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<number[]>([]);
  const [officeConfigDirty, setOfficeConfigDirty] = useState(false);
  const {
    register: registerConsultant,
    handleSubmit: handleConsultantSubmit,
    formState: {
      errors: consultantErrors,
      isDirty: consultantDirty,
      isValid: consultantFormValid,
    },
    reset: resetConsultantForm,
  } = useForm({
    defaultValues: { firstName: "", lastName: "", email: "" },
    mode: "onChange",
  });

  // Placeholder values for RFP preparation fields
  const placeholders = {
    minutesPerPlan: "6.0",
    rfpPrepHours: "3.41",
    peerReviewHours: "1.3",
    additionalHours: "2.66",
    gapAnalysis: "0.5",
    asoOverride: "1.25",
    adaOverride: "3.0",
    dentalDisruptionOverride: "2.0",
  };

  const {
    data,
    isLoading: queryIsLoading,
    error: queryError,
  } = useQuery<ConfigData, Error>({
    queryKey: brokerConfigKey,
    queryFn: async (): Promise<ConfigData> => {
      const { data: response } = await axiosInstance.post<ConfigData>(
        "/brokerportal/v1/brokerClientInfoConfig",
        {
          brokerId,
        },
      );
      return response;
    },
    select: (queryData: ConfigData): ConfigData => {
      const response = { ...queryData };
      delete response["created_at"];
      delete response["updated_at"];
      delete response["deleted_at"];
      delete response.id;
      delete response.onedigitalOfficeMode;
      return response;
    },
    initialData: {},
  });

  const showOfficeConfig = !!data?.onedigitalOffice;

  // Query for analyst teams
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- useAnalystTeams re-exported through JS barrel file
  const {
    data: analystTeams,
    isLoading: teamsLoading,
    error: teamsError,
  } = useAnalystTeams();

  // Query for broker data to get current analyst team
  const {
    data: brokerData,
    isLoading: brokerLoading,
    error: brokerError,
  } = useQuery({
    queryKey: ["broker", brokerId],
    queryFn: async (): Promise<BrokerData> => {
      const { data: response } = await axiosInstance.get<BrokerData>(
        `/brokerportal/v1/brokers/${brokerId}`,
      );
      return response;
    },
    initialData: {} as BrokerData,
  });

  // Query for broker properties
  const {
    data: brokerProperties,
    isLoading: propertiesLoading,
    error: propertiesError,
  } = useQuery({
    queryKey: ["brokerProperties", brokerId],
    queryFn: async (): Promise<BrokerProperties> => {
      const { data: response } = await axiosInstance.get<BrokerProperties>(
        `/brokerportal/v1/brokers/${brokerId}/properties`,
      );
      setLocalProperties(response || {});
      return response;
    },
    initialData: {} as BrokerProperties,
  });

  // Query for all available OneDigital offices
  const { data: allOffices, isLoading: officesLoading } = useQuery({
    queryKey: allOfficesKey,
    queryFn: async (): Promise<OfficeLocation[]> => {
      const { data: response } = await axiosInstance.get<OfficeLocation[]>(
        "/brokerportal/v1/office-locations",
      );
      return response;
    },
    enabled: showOfficeConfig,
    initialData: [],
  });

  // Query for current broker office configuration
  const { data: officeConfig, isLoading: officeConfigLoading } = useQuery({
    queryKey: officeConfigKey,
    queryFn: async (): Promise<OfficeConfig> => {
      const { data: response } = await axiosInstance.get<OfficeConfig>(
        `/brokerportal/v1/brokers/${brokerId}/office-locations`,
      );
      return response;
    },
    enabled: showOfficeConfig,
    initialData: { mode: null, officeIds: [] },
  });

  // Sync local office config state when query data loads
  useEffect(() => {
    if (officeConfig) {
      setOfficeMode(officeConfig.mode);
      setSelectedOfficeIds(officeConfig.officeIds || []);
      setOfficeConfigDirty(false);
    }
  }, [officeConfig]);

  // Mutation for saving office configuration
  const {
    mutate: saveOfficeConfig,
    isPending: isSavingOfficeConfig,
    error: officeConfigError,
    reset: resetOfficeConfigMutation,
  } = useMutation<AxiosResponse, Error, { mode: string; officeIds: number[] }>({
    mutationFn: (config: { mode: string; officeIds: number[] }) =>
      axiosInstance.put(
        `/brokerportal/v1/brokers/${brokerId}/office-locations`,
        config,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: officeConfigKey });
      queryClient.invalidateQueries({ queryKey: brokerConfigKey });
      setOfficeConfigDirty(false);
    },
  });

  const selectedCentroSelection = !!data[CENTRO_SELECT_CARRIER_ENABLED];

  const {
    mutate,
    isPending: isUpdating,
    error: mutationError,
    reset: resetMutation,
  } = useMutation<AxiosResponse, Error, ConfigData>({
    mutationFn: (newData: ConfigData) =>
      axiosInstance.post("brokerportal/v1/upsertBrokerClientConfig", {
        ...newData,
        brokerId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: brokerConfigKey,
      });
    },
    onSettled: () => {
      setSelectedItemKey(null);
    },
  });

  // Mutation for updating broker analyst team
  const {
    mutate: updateBrokerTeam,
    isPending: isUpdatingTeam,
    error: teamMutationError,
    reset: resetTeamMutation,
  } = useMutation<AxiosResponse, Error, number | null>({
    mutationFn: (analystTeamId: number | null) =>
      axiosInstance.patch(`/brokerportal/v1/brokers/${brokerId}`, {
        analystTeamId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["broker", brokerId],
      });
    },
  });

  // Mutation for updating broker consultant
  const {
    mutate: updateBrokerConsultant,
    isPending: isUpdatingConsultant,
    error: consultantMutationError,
    reset: resetConsultantMutation,
  } = useMutation<AxiosResponse, Error, ConsultantUpdateRequest>({
    mutationFn: (consultant: ConsultantUpdateRequest) =>
      axiosInstance.put(
        `/brokerportal/v1/brokers/${brokerId}/consultant`,
        consultant,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["broker", brokerId],
      });
    },
  });

  // Mutation for updating broker properties
  const {
    mutate: updateBrokerProperties,
    isPending: isUpdatingProperties,
    error: propertiesMutationError,
    reset: resetPropertiesMutation,
  } = useMutation<AxiosResponse, Error, UpdateBrokerPropertiesRequest>({
    mutationFn: (properties: UpdateBrokerPropertiesRequest) =>
      axiosInstance.patch(
        `/brokerportal/v1/brokers/${brokerId}/properties`,
        properties,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["brokerProperties", brokerId],
      });
    },
  });

  const onCentroSelect = (): void => {
    setSelectedItemKey(CENTRO_SELECT_CARRIER_ENABLED);
  };

  const onAccept = async (): Promise<void> => {
    if (!selectedItemKey) return;

    const newData: ConfigData = {
      ...data,
      [selectedItemKey]: !data[selectedItemKey],
    };
    mutate(newData);
  };

  const onCancel = (): void => {
    setSelectedItemKey(null);
  };

  const handleAnalystTeamChange = (teamId: number | null): void => {
    updateBrokerTeam(teamId);
  };

  const onConsultantSubmit = (data: ConsultantUpdateRequest): void => {
    updateBrokerConsultant({
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.trim(),
    });
  };

  const debouncedUpdateProperties = useCallback(
    (properties: UpdateBrokerPropertiesRequest): void => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        updateBrokerProperties(properties);
      }, 2000); // 2000ms delay
    },
    [updateBrokerProperties],
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    [],
  );

  // Sync consultant form when broker data loads
  useEffect(() => {
    resetConsultantForm({
      firstName: brokerData?.consultant?.firstName || "",
      lastName: brokerData?.consultant?.lastName || "",
      email: brokerData?.consultant?.email || "",
    });
  }, [brokerData?.consultant, resetConsultantForm]);

  const handlePropertyChange = (
    field: keyof UpdateBrokerPropertiesRequest,
    value: number | null,
  ): void => {
    const updatedProperties = {
      ...brokerProperties,
      ...localProperties,
      [field]: value,
    };

    setLocalProperties((prev) => ({
      ...prev,
      [field]: value,
    }));

    debouncedUpdateProperties(updatedProperties);
  };

  const handleOfficeModeChange = (newMode: string): void => {
    setOfficeMode(newMode);
    if (newMode === "all") {
      setSelectedOfficeIds([]);
    }
    setOfficeConfigDirty(true);
  };

  const handleOfficeSelectionChange = (
    _: unknown,
    selected: OfficeLocation[],
  ): void => {
    setSelectedOfficeIds(selected.map((o) => o.id));
    setOfficeConfigDirty(true);
  };

  const handleSaveOfficeConfig = (): void => {
    if (!officeMode) return;
    saveOfficeConfig({
      mode: officeMode,
      officeIds: officeMode === "selected" ? selectedOfficeIds : [],
    });
  };

  if (
    [queryIsLoading, teamsLoading, brokerLoading, propertiesLoading].some(
      Boolean,
    )
  ) {
    return <Loader />;
  }

  const errorResetPairs: [Error | null, VoidFunction][] = [
    [mutationError, resetMutation],
    [teamMutationError, resetTeamMutation],
    [consultantMutationError, resetConsultantMutation],
    [propertiesMutationError, resetPropertiesMutation],
    [officeConfigError, resetOfficeConfigMutation],
  ];

  const handleDismissError = (): void => {
    errorResetPairs.filter(([err]) => err).forEach(([, reset]) => reset());
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Error union from multiple query/mutation sources
  const error =
    [
      queryError,
      mutationError,
      teamsError,
      brokerError,
      teamMutationError,
      consultantMutationError,
      propertiesError,
      propertiesMutationError,
      officeConfigError,
    ].find(Boolean) ?? null;

  const hiddenConfigKeys = new Set(HIDDEN_CONFIG_KEYS);
  const showConsultant = brokerData?.type === ONE_DIGITAL;

  return (
    <Box>
      {!!selectedItemKey && (
        <ConfirmationModal
          onAccept={onAccept}
          onCancel={onCancel}
          open={!!selectedItemKey}
          show={!!data[selectedItemKey]}
          label={configKeysValues[selectedItemKey]}
          loading={isUpdating}
          key={selectedItemKey}
        />
      )}
      {error && (
        <Box mt={2} mb={2}>
          <Alert severity="error" onClose={handleDismissError}>
            {extractErrorMessage(error)}
          </Alert>{" "}
        </Box>
      )}

      {showConsultant && (
        <ConsultantSection
          handleConsultantSubmit={handleConsultantSubmit}
          onConsultantSubmit={onConsultantSubmit}
          registerConsultant={registerConsultant}
          consultantErrors={consultantErrors}
          consultantDirty={consultantDirty}
          consultantFormValid={consultantFormValid}
          isUpdatingConsultant={isUpdatingConsultant}
        />
      )}

      <Box mb={3}>
        <Typography variant="display-small">
          Client Information Fields
        </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fields</TableCell>
              <TableCell>Show / Hide</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(data)
              .filter((dataKey) => !hiddenConfigKeys.has(dataKey))
              .map((itemKey) => (
                <TableRow key={itemKey}>
                  <TableCell>{configKeysValues[itemKey]}</TableCell>
                  <TableCell>
                    <Switch
                      checked={!!data[itemKey]}
                      onChange={() => setSelectedItemKey(itemKey)}
                      slotProps={{
                        input: {
                          "aria-label": `switch-${itemKey}`,
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showOfficeConfig && (
        <OfficeConfigurationSection
          officesLoading={officesLoading}
          officeConfigLoading={officeConfigLoading}
          officeMode={officeMode}
          selectedOfficeIds={selectedOfficeIds}
          allOffices={allOffices}
          officeConfigDirty={officeConfigDirty}
          isSavingOfficeConfig={isSavingOfficeConfig}
          handleOfficeModeChange={handleOfficeModeChange}
          handleOfficeSelectionChange={handleOfficeSelectionChange}
          handleSaveOfficeConfig={handleSaveOfficeConfig}
        />
      )}

      <Box mb={3} mt={3}>
        <Typography variant="display-small">Carriers</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "56%" }}>Feature</TableCell>
              <TableCell>Show / Hide</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Centro Carrier Selection</TableCell>
              <TableCell>
                <Switch
                  checked={selectedCentroSelection}
                  onChange={() => onCentroSelect()}
                  slotProps={{
                    input: {
                      "aria-label": `switch-${CENTRO_SELECT_CARRIER_ENABLED}`,
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box mb={3} mt={3}>
        <Typography variant="display-small">Assigned Analyst Team</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "56%" }}>Team Assignment</TableCell>
              <TableCell>Current Selection</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Analyst Team</TableCell>
              <TableCell>
                <FormControl fullWidth size="small" disabled={isUpdatingTeam}>
                  <InputLabel id="analyst-team-select-label">
                    Select Team
                  </InputLabel>
                  <Select
                    labelId="analyst-team-select-label"
                    value={brokerData?.analystTeamId?.toString() || ""}
                    label="Select Team"
                    onChange={(e) => {
                      const value = e.target.value as string;
                      handleAnalystTeamChange(
                        value === "" ? null : Number(value),
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>No Team Assigned</em>
                    </MenuItem>
                    {analystTeams?.teams?.map((team) => (
                      <MenuItem key={String(team.id)} value={String(team.id)}>
                        {team.name}
                        {team.description && ` - ${team.description}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <RfpPreparationSection
        localProperties={localProperties}
        brokerProperties={brokerProperties}
        placeholders={placeholders}
        handlePropertyChange={handlePropertyChange}
        isUpdatingProperties={isUpdatingProperties}
      />
    </Box>
  );
};

type ConsultantFormValues = {
  firstName: string;
  lastName: string;
  email: string;
};

type ConsultantSectionProps = {
  handleConsultantSubmit: UseFormHandleSubmit<ConsultantFormValues>;
  onConsultantSubmit: (data: ConsultantUpdateRequest) => void;
  registerConsultant: UseFormRegister<ConsultantFormValues>;
  consultantErrors: FieldErrors<ConsultantFormValues>;
  consultantDirty: boolean;
  consultantFormValid: boolean;
  isUpdatingConsultant: boolean;
};

const ConsultantSection = ({
  handleConsultantSubmit,
  onConsultantSubmit,
  registerConsultant,
  consultantErrors,
  consultantDirty,
  consultantFormValid,
  isUpdatingConsultant,
}: ConsultantSectionProps): ReactElement => {
  const renderTextField = (
    label: string,
    field: "firstName" | "lastName" | "email",
    validate?: (v: string) => boolean | string,
    type?: string,
  ): ReactElement => (
    <Grid size={{ xs: 12, md: 6 }}>
      <TextField
        label={label}
        fullWidth
        required
        type={type}
        {...registerConsultant(field, {
          required: `${label} is required`,
          validate:
            validate ??
            ((v: string) => v.trim() !== "" || `${label} is required`),
        })}
        error={!!consultantErrors[field]}
        helperText={
          consultantErrors[field]?.message
            ? String(consultantErrors[field].message)
            : undefined
        }
        disabled={isUpdatingConsultant}
        autoComplete="off"
      />
    </Grid>
  );

  return (
    <>
      <Box mb={3}>
        <Typography variant="display-small">Centro Consultant</Typography>
      </Box>
      <Paper
        component="form"
        onSubmit={handleConsultantSubmit(onConsultantSubmit)}
        sx={{ p: 3, mb: 3 }}
      >
        <Grid container spacing={3}>
          {renderTextField("First Name", "firstName")}
          {renderTextField("Last Name", "lastName")}
          {renderTextField(
            "Email",
            "email",
            (v) => isValidEmail(v.trim()) || "Invalid email address",
            "email",
          )}
        </Grid>
        {consultantDirty && (
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="primary"
              type="submit"
              disabled={isUpdatingConsultant || !consultantFormValid}
            >
              {isUpdatingConsultant ? "Saving..." : "Save Consultant"}
            </Button>
          </Box>
        )}
      </Paper>
    </>
  );
};

type OfficeConfigurationSectionProps = {
  officesLoading: boolean;
  officeConfigLoading: boolean;
  officeMode: string | null;
  selectedOfficeIds: number[];
  allOffices: OfficeLocation[];
  officeConfigDirty: boolean;
  isSavingOfficeConfig: boolean;
  handleOfficeModeChange: (mode: string) => void;
  handleOfficeSelectionChange: (_: unknown, selected: OfficeLocation[]) => void;
  handleSaveOfficeConfig: () => void;
};

const OfficeConfigurationSection = ({
  officesLoading,
  officeConfigLoading,
  officeMode,
  selectedOfficeIds,
  allOffices,
  officeConfigDirty,
  isSavingOfficeConfig,
  handleOfficeModeChange,
  handleOfficeSelectionChange,
  handleSaveOfficeConfig,
}: OfficeConfigurationSectionProps): ReactElement => (
  <>
    <Box mb={3} mt={3}>
      <Typography variant="display-small">
        ONEDIGITAL Office Configuration
      </Typography>
    </Box>
    <Paper sx={{ p: 3 }}>
      {officesLoading || officeConfigLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body-large" mb={2}>
            Select which offices are available when users submit RFPs for this
            broker.
          </Typography>
          <FormControl>
            <RadioGroup
              value={officeMode || ""}
              onChange={(e) => handleOfficeModeChange(e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="All offices"
              />
              <FormControlLabel
                value="selected"
                control={<Radio />}
                label="Select specific offices"
              />
            </RadioGroup>
          </FormControl>

          {officeMode === "selected" && (
            <Box mt={2}>
              <Autocomplete
                multiple
                options={allOffices}
                value={allOffices.filter((o: OfficeLocation) =>
                  selectedOfficeIds.includes(o.id),
                )}
                onChange={handleOfficeSelectionChange}
                getOptionLabel={(option: OfficeLocation) => option.location}
                isOptionEqualToValue={(
                  option: OfficeLocation,
                  value: OfficeLocation,
                ) => option.id === value.id}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {option.location}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="sm"
                    label="Select offices"
                    placeholder="Search offices..."
                  />
                )}
              />
            </Box>
          )}

          {officeConfigDirty && (
            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                variant="primary"
                onClick={handleSaveOfficeConfig}
                disabled={
                  isSavingOfficeConfig ||
                  !officeMode ||
                  (officeMode === "selected" && selectedOfficeIds.length === 0)
                }
              >
                {isSavingOfficeConfig
                  ? "Saving..."
                  : "Save Office Configuration"}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  </>
);

type RfpPropertyRow = {
  label: string;
  field: keyof UpdateBrokerPropertiesRequest;
  placeholder: string;
};

const rfpPropertyRows: RfpPropertyRow[] = [
  { label: "Minutes Per Plan", field: "minutesPerPlan", placeholder: "6.0" },
  { label: "RFP Prep Points", field: "rfpPrepHours", placeholder: "3.41" },
  { label: "Peer Review Points", field: "peerReviewHours", placeholder: "1.3" },
  { label: "Additional Points", field: "additionalHours", placeholder: "2.66" },
  { label: "Gap Analysis", field: "gapAnalysis", placeholder: "0.5" },
  { label: "ASO Override", field: "asoOverride", placeholder: "1.25" },
  { label: "ADA Override", field: "adaOverride", placeholder: "3.0" },
  {
    label: "Dental Disruption Override",
    field: "dentalDisruptionOverride",
    placeholder: "2.0",
  },
];

type RfpPreparationSectionProps = {
  localProperties: UpdateBrokerPropertiesRequest;
  brokerProperties: BrokerProperties;
  placeholders: Record<string, string>;
  handlePropertyChange: (
    field: keyof UpdateBrokerPropertiesRequest,
    value: number | null,
  ) => void;
  isUpdatingProperties: boolean;
};

const RfpPreparationSection = ({
  localProperties,
  brokerProperties,
  handlePropertyChange,
  isUpdatingProperties,
}: RfpPreparationSectionProps): ReactElement => (
  <>
    <Box mb={3} mt={3}>
      <Typography variant="display-small">RFP Preparation</Typography>
    </Box>
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "56%" }}>Property</TableCell>
            <TableCell>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rfpPropertyRows.map((row) => (
            <TableRow key={row.field}>
              <TableCell>{row.label}</TableCell>
              <TableCell>
                <TextField
                  type="number"
                  size="sm"
                  fullWidth
                  placeholder={row.placeholder}
                  value={
                    localProperties[row.field] ??
                    brokerProperties?.[row.field] ??
                    ""
                  }
                  onChange={(e) => {
                    const value =
                      e.target.value === "" ? null : parseFloat(e.target.value);
                    handlePropertyChange(row.field, value);
                  }}
                  disabled={isUpdatingProperties}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.1,
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

type ConfirmationModalProps = {
  onCancel: VoidFunction;
  onAccept: VoidFunction;
  open: boolean;
  show: boolean;
  label: string;
  loading: boolean;
};

const ConfirmationModal = ({
  onCancel,
  onAccept,
  open,
  show,
  label,
  loading,
}: ConfirmationModalProps): ReactElement => {
  const needsToShow = !show;

  const messageToShow = (): string => {
    if (label === configKeysValues.centro_select_carrier_enabled) {
      return !needsToShow
        ? "will no longer be shown as an option in RFPs for this broker."
        : "will be added as an option in RFPs for this broker.";
    } else if (label === configKeysValues.new_analyst_workspace) {
      return !needsToShow
        ? "will no longer be visible within submitted RFPs."
        : "will be visible within submitted RFPs.";
    }

    return `will no longer be ${
      needsToShow ? "hidden" : "shown"
    } under Client Information`;
  };

  const oneDigitalMessage = (): string | null => {
    if (label === configKeysValues.onedigitalOffice) {
      return "**Only use this Field for Onedigital Markets**";
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="confirmation-conf-title"
      aria-describedby="confirmation-conf-description"
      maxWidth="sm"
    >
      <DialogTitle id="confirmation-conf-title">{`${
        needsToShow ? "Show" : "Hide"
      } ${label}`}</DialogTitle>
      <DialogContent id="confirmation-conf-description">
        {oneDigitalMessage() && (
          <Typography mb={3} textTransform="uppercase" variant="body-large">
            <strong>{oneDigitalMessage()}</strong>
          </Typography>
        )}
        <Typography mb={3} variant="body-large">
          <Typography component="span">
            <strong>{label}</strong>
          </Typography>{" "}
          {messageToShow()}
        </Typography>
        <Typography variant="body-large">
          Are you sure you want to {needsToShow ? "show" : "hide"} it?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="tertiary">
          No, cancel
        </Button>
        <Button
          onClick={onAccept}
          variant="primary"
          disabled={loading}
          loading={loading}
          loadingPosition="start"
        >
          Yes, {needsToShow ? "Show" : "Hide"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { Configuration };
